// Experimental SSE-based job progress hook with polling fallback
import { useEffect, useRef, useState } from 'react';
import { JobEvent, fetchJobEvents } from '../api/jobsApi';
import { sseReconnectDelay } from '../../../../utils/sseReconnect';

export interface UseJobProgressStreamOptions {
  fallbackPollMs?: number;
  autoStopOnDone?: boolean;
  useSSE?: boolean; // enable/disable
}

export interface UseJobProgressStreamResult {
  events: JobEvent[];
  latest: JobEvent | null;
  loading: boolean;
  error: any;
  percent: number | null;
  stage?: string;
  usingSSE: boolean;
  reset(): void;
  /** Immediately nudge the backend: one-off poll; if quiet, bounce SSE. Does not clear state. */
  retryNow(): Promise<void>;
}

export function useJobProgressStream(runId: string | number | null | undefined, opts: UseJobProgressStreamOptions = {}): UseJobProgressStreamResult {
  const { fallbackPollMs = 4000, autoStopOnDone = true, useSSE = true } = opts;
  const [events, setEvents] = useState<JobEvent[]>([]);
  const [latest, setLatest] = useState<JobEvent | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<any>(null);
  const [usingSSE, setUsingSSE] = useState(false);
  // Internal knob to force-restart the SSE/poll wiring (used for rate-limit nudges)
  const [restartKey, setRestartKey] = useState(0);
  const pollTimer = useRef<any>(null);
  const lastSeqRef = useRef<number>(0);
  const esRef = useRef<EventSource | null>(null);
  const sseAttemptRef = useRef(0);
  const closeConnections = () => {
    if (pollTimer.current) { try { clearInterval(pollTimer.current); } catch {} pollTimer.current = null; }
    if (esRef.current) { try { esRef.current.close(); } catch {} esRef.current = null; }
  };

  const reset = () => {
    setEvents([]); setLatest(null); setError(null); lastSeqRef.current = 0;
    if (pollTimer.current) { clearInterval(pollTimer.current); pollTimer.current = null; }
    if (esRef.current) { esRef.current.close(); esRef.current = null; }
  };

  useEffect(() => { reset(); }, [runId]);

  useEffect(() => {
    if (!runId) return; setLoading(true);

    const startPolling = () => {
      setUsingSSE(false);
      const poll = async () => {
        try {
          const data = await fetchJobEvents(runId, lastSeqRef.current || undefined);
          if (data.events?.length) {
            lastSeqRef.current = data.lastSeq;
            setEvents(prev => [...prev, ...data.events]);
            setLatest(data.events[data.events.length - 1]);
          }
            if (autoStopOnDone) {
            const hasHardDone = (
              data.done === true ||
              data.events.some((e) => {
                const st = String((e.stage || '')).toLowerCase();
                const err = String((e as any).error || '').toLowerCase();
                const rateLimited = /rate[ _-]?limited/.test(st) || /rate[ _-]?limited/.test(err);
                if (/^(done|completed|job_completed|success)$/.test(st)) return true;
                if (/^(failed|error)$/.test(st) && !rateLimited) return true; // don't stop on transient rate_limited
                return false;
              })
            );
            if (hasHardDone) {
            if (pollTimer.current) { clearInterval(pollTimer.current); pollTimer.current = null; }
            }
          }
        } catch (e) { setError(e); }
        finally { setLoading(false); }
      };
      poll();
      pollTimer.current = setInterval(poll, fallbackPollMs);
    };

    if (!useSSE || typeof EventSource === 'undefined') {
      startPolling(); return;
    }

    try {
      // Assuming bff base path '/bff'
      const es = new EventSource(`/bff/jobs/${runId}/stream`);
      esRef.current = es; setUsingSSE(true); setLoading(false);

      es.addEventListener('progress', (ev: MessageEvent) => {
        try {
          const evt: any = JSON.parse(ev.data);
          const seq = evt.seq || (lastSeqRef.current + 1);
          if (seq <= lastSeqRef.current) return;
          lastSeqRef.current = seq;
          setEvents(prev => [...prev, { ...evt, seq }]);
          setLatest(evt);
        } catch (e) { /* ignore parse */ }
      });
      es.addEventListener('done', (ev: MessageEvent) => {
        if (!autoStopOnDone) return;
        try {
          const data = JSON.parse(ev.data || '{}');
          // Only close automatically if new protocol indicates final
          if (data.stack_final === true || data.done === true) {
            es.close();
          }
        } catch {
          es.close();
        }
      });
      es.onerror = (_e) => {
        // Fallback to polling with jitter delay (H3: prevent thundering-herd reconnects)
        if (!usingSSE) return;
        es.close();
        const delay = sseReconnectDelay(sseAttemptRef.current);
        sseAttemptRef.current++;
        setTimeout(() => startPolling(), delay);
      };
    } catch (e) {
      setError(e);
      startPolling();
    }
    // Do not reset state on wiring changes (useSSE/fallbackPollMs/restartKey); only close connections.
    return () => { closeConnections(); };
  }, [runId, useSSE, autoStopOnDone, fallbackPollMs, restartKey]);

  // Nudge logic: when we detect a rate-limited state that reaches (or is reported as) 0s,
  // proactively poke the backend once, and if still quiet on SSE, bounce the connection.
  const nudgeSeqRef = useRef<number>(0);
  useEffect(() => {
    if (!runId || !latest) return;
    const st = String((latest.stage || '')).toLowerCase();
    const err = String((latest as any).error || '').toLowerCase();
    const msg = String((latest as any).message || '').toLowerCase();
    const isRateLimited = /rate[ _-]?limited/.test(st) || /rate[ _-]?limited/.test(err) || /rate[ _-]?limited/.test(msg);
    if (!isRateLimited) return;
    const raw = (latest as any);
    const v = raw.wait_seconds ?? raw.retry_in_seconds ?? raw.backoff_seconds ?? raw.retry_after ?? raw.wait_remain_sec ?? null;
    const ws = (typeof v === 'number') ? v : (typeof v === 'string' ? Number(v) : null);
    if (nudgeSeqRef.current === (latest.seq || 0)) return; // already scheduled for this event
    nudgeSeqRef.current = (latest.seq || 0);
    const delayMs = (ws != null && isFinite(ws) && ws > 0) ? (Math.max(0, ws) * 1000 + 500) : 1500;
    const t = setTimeout(async () => {
      try {
        // One-off poll to collect any new events that might have been missed due to idle SSE
        const data = await fetchJobEvents(runId, lastSeqRef.current || undefined);
        if (data?.events?.length) {
          lastSeqRef.current = data.lastSeq;
          setEvents(prev => [...prev, ...data.events]);
          setLatest(data.events[data.events.length - 1]);
          return;
        }
        // If still nothing and we were relying on SSE, bounce the connection to resubscribe
        if (useSSE && esRef.current) {
          try { esRef.current.close(); } catch {}
          esRef.current = null;
          setRestartKey(k => k + 1);
        }
      } catch {
        // ignore; next backend event will advance naturally
      }
    }, delayMs);
    return () => clearTimeout(t);
  }, [runId, latest, useSSE]);

  // Composite percent: if stack info present, blend completed steps + current step percent
  let percent: number | null = null;
  if (latest) {
    const current = latest as any;
    // Prefer overall_progress when present (backend already derives percent from it)
    if (typeof current.overall_progress === 'number') {
      percent = Math.round(current.overall_progress * 100);
    } else if (typeof current.percent === 'number') {
      percent = current.percent;
    } else if (typeof current.progress === 'number') {
      percent = Math.round(current.progress * 100);
    }
    // If overall_progress not provided, we can still estimate from stack fields
    if (percent == null && current.stack_steps && current.stack_steps > 1) {
      const steps = current.stack_steps;
      const completed = current.stack_completed_steps != null ? current.stack_completed_steps : (current.stack_step_index != null ? current.stack_step_index : 0);
      const stepFrac = typeof current.stage_progress === 'number' ? current.stage_progress : (typeof current.progress === 'number' ? current.progress : (typeof current.percent === 'number' ? current.percent / 100 : 0));
      const overall = ((completed) + stepFrac) / steps;
      percent = Math.min(100, Math.max(0, Math.round(overall * 100)));
    }
  }

  const retryNow = async () => {
    if (!runId) return;
    try {
      const data = await fetchJobEvents(runId, lastSeqRef.current || undefined);
      if (data?.events?.length) {
        lastSeqRef.current = data.lastSeq;
        setEvents(prev => [...prev, ...data.events]);
        setLatest(data.events[data.events.length - 1]);
        return;
      }
      if (useSSE && esRef.current) {
        try { esRef.current.close(); } catch {}
        esRef.current = null;
        setRestartKey(k => k + 1);
      }
    } catch {
      // swallow; next backend event will advance naturally
    }
  };

  return { events, latest, loading, error, percent, stage: latest?.stage, usingSSE, reset, retryNow };
}

export type { JobEvent } from '../api/jobsApi';
